import { toSVG } from "@bwip-js/browser";
import type { StaffVisitPacketBundle } from "./staff-operations-gateway";
import { QRCodeSVG } from "qrcode.react";
import { PortalPrintDocument } from "../../PortalPrintDocument";

function VisitCode({ bundle }: { readonly bundle: StaffVisitPacketBundle }) {
  if (bundle.codeFormat === "QR") {
    return (
      <QRCodeSVG
        value={bundle.codePayload}
        size={152}
        level="M"
        marginSize={4}
        title="Opaque visit code"
      />
    );
  }
  const svg = toSVG({
    bcid: "datamatrix",
    text: bundle.codePayload,
    scale: 4,
    paddingwidth: 4,
    paddingheight: 4,
    backgroundcolor: "ffffff",
    barcolor: "000000",
  });
  return (
    <span
      className="staff-visit-code__matrix"
      aria-label="Opaque visit Data Matrix"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function Readiness({ bundle }: { readonly bundle: StaffVisitPacketBundle }) {
  const { readiness } = bundle.packet;
  return (
    <dl className="staff-packet-readiness">
      <div>
        <dt>Forms</dt>
        <dd>{readiness.forms.replaceAll("_", " ")}</dd>
      </div>
      <div>
        <dt>Consent</dt>
        <dd>{readiness.consent.replaceAll("_", " ")}</dd>
      </div>
      <div>
        <dt>Preparation</dt>
        <dd>{readiness.preparation.replaceAll("_", " ")}</dd>
      </div>
    </dl>
  );
}

export function StaffVisitPacketPrintView({
  bundles,
}: {
  readonly bundles: readonly StaffVisitPacketBundle[];
}) {
  const auditedBundles = bundles.filter(
    (
      bundle,
    ): bundle is StaffVisitPacketBundle & {
      readonly printAudit: NonNullable<StaffVisitPacketBundle["printAudit"]>;
    } => bundle.printAudit !== null,
  );
  return (
    <div className="staff-packet-print-set">
      {auditedBundles.map((bundle, index) => (
        <PortalPrintDocument
          key={bundle.packet.packetId}
          documentId={`staff-visit-packet-${index + 1}`}
          kind="visit-packet"
          title="Guest visit packet"
          subtitle="Minimum-necessary operational view"
          metadata={[
            { label: "Generated", value: bundle.packet.generatedAt },
            {
              label: "Printer",
              value: bundle.printerDisplayLabel,
            },
            { label: "Print intent", value: bundle.printAudit.printedAt },
          ]}
          footer={
            <>
              Confidential operational packet · Return to the secured collection
              point after use · Destroy through the approved locked shredding
              workflow · Operational use expires {bundle.printAudit.expiresAt}
            </>
          }
        >
          <div className="staff-packet-layout">
            <section>
              <h2>{bundle.packet.guestDisplayName}</h2>
              <dl className="staff-packet-details">
                <div>
                  <dt>Appointment</dt>
                  <dd>
                    {bundle.packet.startsAt} · {bundle.packet.durationMinutes}{" "}
                    minutes
                  </dd>
                </div>
                <div>
                  <dt>Location</dt>
                  <dd>{bundle.packet.locationLabel}</dd>
                </div>
                <div>
                  <dt>Room / station</dt>
                  <dd>
                    {[bundle.packet.roomLabel, bundle.packet.stationLabel]
                      .filter(Boolean)
                      .join(" · ") || "Unassigned"}
                  </dd>
                </div>
                <div>
                  <dt>Staff</dt>
                  <dd>{bundle.packet.staffDisplayNames.join(", ")}</dd>
                </div>
                <div>
                  <dt>Modalities</dt>
                  <dd>{bundle.packet.modalityLabels.join(", ")}</dd>
                </div>
              </dl>
              <Readiness bundle={bundle} />
              <h3>Setup</h3>
              <ul>
                {bundle.packet.setupRequirements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {bundle.packet.accommodations.length > 0 && (
                <>
                  <h3>Approved accommodations</h3>
                  <ul>
                    {bundle.packet.accommodations.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {bundle.packet.operationalNotes.length > 0 && (
                <>
                  <h3>Approved operational notes</h3>
                  <ul>
                    {bundle.packet.operationalNotes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </section>
            <aside className="staff-visit-code">
              <VisitCode bundle={bundle} />
              <strong>
                {bundle.codeFormat === "QR" ? "QR Code" : "Data Matrix"}
              </strong>
              <span>Opaque, expiring, visit-scoped code</span>
              <span>No guest data or document URL is encoded.</span>
            </aside>
          </div>
        </PortalPrintDocument>
      ))}
    </div>
  );
}
